import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/useLanguage';
import LanguageSwitcher from '../components/LanguageSwitcher';

/**
 * Frame for the standalone legal documents.
 *
 * The actual copy lives as plain static HTML in /public/legal
 * (privacy-*.html, terms-*.html, styled by legal.css) so it keeps
 * working even if the SPA fails to load. This page just wraps the
 * matching file for the active language in an <iframe>, on the same
 * cream `bg-tan` + hero-pattern scene the home page uses.
 *
 * Chrome is deliberately minimal: logo (links home) + the shared
 * LanguageSwitcher. Switching language in the header swaps the
 * framed document; no in-page navigation of its own.
 */
type LegalDoc = 'privacy' | 'tos';

const DOC_SOURCES: Record<LegalDoc, Record<'en' | 'th', string>> = {
  privacy: {
    en: '/legal/privacy-en.html',
    th: '/legal/privacy-th.html',
  },
  tos: {
    en: '/legal/tos-en.html',
    th: '/legal/tos-th.html',
  },
};

export default function LegalPage({ doc }: { doc: LegalDoc }) {
  const { lang, t } = useLanguage();
  const title = t(doc === 'privacy' ? 'legal.privacy.title' : 'legal.tos.title');
  const src = DOC_SOURCES[doc][lang];

  useEffect(() => {
    const previous = document.title;
    document.title = `${title} | I Love Cooking`;
    return () => {
      document.title = previous;
    };
  }, [title]);

  return (
    <div className="relative isolate flex h-dvh w-full flex-col overflow-hidden bg-tan">
      {/* Background pattern overlay — same asset HeroSection /
          UnderConstruction use, behind everything. */}
      <img
        src="/images/hero-bg-pattern.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover"
      />

      {/* Minimal header — logo (home link) + language toggle only. */}
      <header className="flex shrink-0 items-center justify-between px-6 py-3 md:px-10 lg:py-4">
        <Link
          to="/"
          aria-label={t('legal.backHome')}
          className="flex items-center transition-transform duration-200 ease-out hover:scale-105 active:scale-95"
        >
          <img
            src="/images/logo.png"
            alt="I Love Cooking"
            className="h-10 w-auto select-none object-contain lg:h-[64px]"
            draggable={false}
          />
        </Link>
        <LanguageSwitcher />
      </header>

      {/* Framed document. `key={src}` remounts the iframe on language
          change so switching doesn't pile up iframe history entries.
          The iframe scrolls its own content — the page itself stays
          pinned to the viewport. */}
      <main className="flex min-h-0 flex-1 flex-col px-4 pb-4 md:px-10 md:pb-8">
        <iframe
          key={src}
          src={src}
          title={title}
          className="min-h-0 w-full flex-1 rounded-[20px] border-[3px] border-white bg-[#f6eadf] shadow-[0_13px_0_0_rgba(0,0,0,0.15)]"
        />
      </main>
    </div>
  );
}
